import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { CrewTemplateLogo } from '@/components/crews/crew-template-logo'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { getErrorMessage } from '@/lib/api'
import { ensureUniqueCrewTemplateCode } from '@/lib/crew-template-code'
import { categoriesApi } from '@/services/categories'
import { catalogRolesApi, resolveCatalogRoleSelectId } from '@/services/catalog-roles'
import crewsApi, { type CrewTemplatePayload } from '@/services/crews'
import type { CrewTemplate, CrewTemplateSlot } from '@/types'

type SlotDraft = {
  id?: string | number
  skill_role: string
  skill_role_unique_id?: string | null
  skill_role_name?: string | null
  quantity: string
  is_primary: boolean
  slot_label: string
  ordering: number
}

const emptySlot = (ordering: number): SlotDraft => ({
  skill_role: '',
  quantity: '1',
  is_primary: ordering === 0,
  slot_label: '',
  ordering,
})

type CrewTemplatesPanelProps = {
  serviceInstanceId?: string | null
  onManageMembers?: (templateId: string) => void
}

export function CrewTemplatesPanel({ serviceInstanceId, onManageMembers }: CrewTemplatesPanelProps) {
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<CrewTemplate | null>(null)
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [description, setDescription] = useState('')
  const [jobCategory, setJobCategory] = useState('none')
  const [active, setActive] = useState(true)
  const [slots, setSlots] = useState<SlotDraft[]>([emptySlot(0)])
  const codeManuallyEditedRef = useRef(false)

  const templatesQuery = useQuery({
    queryKey: ['crew-templates', serviceInstanceId],
    queryFn: () => crewsApi.listTemplates({ service_instance_id: serviceInstanceId ?? undefined }),
    enabled: Boolean(serviceInstanceId),
  })

  const crewsQuery = useQuery({
    queryKey: ['crews', serviceInstanceId],
    queryFn: () => crewsApi.listCrews({ service_instance_id: serviceInstanceId ?? undefined }),
    enabled: Boolean(serviceInstanceId),
  })

  const memberCountByTemplateId = useMemo(() => {
    const counts = new Map<string, number>()
    for (const crew of crewsQuery.data ?? []) {
      const templateId = String(crew.crew_template)
      const memberCount = crew.member_count ?? crew.members?.length ?? 0
      counts.set(templateId, (counts.get(templateId) ?? 0) + memberCount)
    }
    return counts
  }, [crewsQuery.data])

  const categoriesQuery = useQuery({
    queryKey: ['job-categories', serviceInstanceId],
    queryFn: () => categoriesApi.getCategories(),
    enabled: Boolean(serviceInstanceId),
  })

  const categoryOptions = useMemo(
    () => (categoriesQuery.data?.results ?? []).filter((category) => category.active !== false),
    [categoriesQuery.data?.results],
  )

  const categoryNameById = useMemo(
    () => new Map(categoryOptions.map((category) => [String(category.id), String(category.name ?? '').trim()])),
    [categoryOptions],
  )

  const rolesQuery = useQuery({
    queryKey: ['catalog-roles', serviceInstanceId],
    queryFn: () => catalogRolesApi.list({
      serviceInstanceId: serviceInstanceId ?? undefined,
      includeHidden: true,
    }),
    enabled: Boolean(serviceInstanceId),
  })

  const roleOptions = useMemo(() => rolesQuery.data ?? [], [rolesQuery.data])

  useEffect(() => {
    if (!dialogOpen || roleOptions.length === 0) {
      return
    }

    setSlots((current) => {
      let changed = false
      const next = current.map((slot) => {
        const resolvedSkillRole = resolveCatalogRoleSelectId(slot, roleOptions)
        if (resolvedSkillRole && resolvedSkillRole !== slot.skill_role) {
          changed = true
          return { ...slot, skill_role: resolvedSkillRole }
        }
        return slot
      })
      return changed ? next : current
    })
  }, [dialogOpen, roleOptions])

  const existingTemplateCodes = useMemo(
    () => (templatesQuery.data ?? []).map((template) => template.code).filter(Boolean),
    [templatesQuery.data],
  )

  const buildTemplateCodeSource = (templateName: string, categoryId?: string) => {
    const normalizedName = templateName.trim()
    if (!normalizedName) {
      return ''
    }

    if (!categoryId || categoryId === 'none') {
      return normalizedName
    }

    const categoryName = categoryNameById.get(categoryId)
    return categoryName ? `${categoryName}_${normalizedName}` : normalizedName
  }

  const suggestTemplateCode = (nextName: string, excludeCode?: string | null) =>
    ensureUniqueCrewTemplateCode(buildTemplateCodeSource(nextName, jobCategory), existingTemplateCodes, excludeCode)

  const handleNameChange = (nextName: string) => {
    setName(nextName)
    if (!codeManuallyEditedRef.current) {
      setCode(nextName.trim() ? suggestTemplateCode(nextName, editing?.code) : '')
    }
  }

  const handleCodeChange = (nextCode: string) => {
    codeManuallyEditedRef.current = true
    setCode(nextCode)
  }

  const handleJobCategoryChange = (value: string) => {
    setJobCategory(value)
    if (!codeManuallyEditedRef.current) {
      setCode(
        name.trim()
          ? ensureUniqueCrewTemplateCode(
              buildTemplateCodeSource(name, value),
              existingTemplateCodes,
              editing?.code,
            )
          : '',
      )
    }
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!serviceInstanceId) {
        throw new Error('Service instance is required')
      }
      const validSlots = slots.filter((slot) => slot.skill_role)
      if (validSlots.length === 0) {
        throw new Error('Add at least one team role slot')
      }
      const payload: CrewTemplatePayload = {
        service_instance: serviceInstanceId,
        name: name.trim(),
        code: (code.trim() || suggestTemplateCode(name.trim(), editing?.code)).trim(),
        description: description.trim(),
        job_category: jobCategory === 'none' ? null : jobCategory,
        active,
        slots: validSlots
          .map((slot, index) => ({
            id: slot.id,
            skill_role: slot.skill_role,
            quantity: Number(slot.quantity) || 1,
            is_primary: slot.is_primary,
            slot_label: slot.slot_label.trim(),
            ordering: index,
          })) as CrewTemplateSlot[],
      }
      if (editing?.id) {
        return crewsApi.updateTemplate(editing.id, payload)
      }
      return crewsApi.createTemplate(payload)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['crew-templates'] })
      setDialogOpen(false)
      toast.success(editing ? 'Team updated' : 'Team created')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string | number) => crewsApi.deleteTemplate(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['crew-templates'] })
      toast.success('Team deleted')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const openCreate = () => {
    setEditing(null)
    codeManuallyEditedRef.current = false
    setName('')
    setCode('')
    setDescription('')
    setJobCategory('none')
    setActive(true)
    setSlots([emptySlot(0)])
    setDialogOpen(true)
  }

  const openEdit = (template: CrewTemplate) => {
    setEditing(template)
    codeManuallyEditedRef.current = true
    setName(template.name)
    setCode(template.code)
    setDescription(template.description ?? '')
    setJobCategory(template.job_category ? String(template.job_category) : 'none')
    setActive(template.active ?? true)
    setSlots(
      (template.slots ?? []).length
        ? (template.slots ?? []).map((slot, index) => ({
            id: slot.id,
            skill_role: resolveCatalogRoleSelectId(slot, roleOptions),
            skill_role_unique_id: slot.skill_role_unique_id ?? null,
            skill_role_name: slot.skill_role_name ?? null,
            quantity: String(slot.quantity ?? 1),
            is_primary: Boolean(slot.is_primary),
            slot_label: slot.slot_label ?? '',
            ordering: slot.ordering ?? index,
          }))
        : [emptySlot(0)],
    )
    setDialogOpen(true)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Teams</h2>
          <p className="text-sm text-muted-foreground">Define team role blueprints such as 1 senior technician and 2 helpers.</p>
        </div>
        <Button onClick={openCreate} disabled={!serviceInstanceId}>
          <Plus className="mr-2 h-4 w-4" />
          New team
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {(templatesQuery.data ?? []).map((template) => (
          <Card key={String(template.id)}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-start gap-3">
                  <CrewTemplateLogo logoUrl={template.logo_url} name={template.name} />
                  <div className="min-w-0">
                    <CardTitle className="text-base">{template.name}</CardTitle>
                    {template.job_category_name ? (
                      <p className="text-xs text-muted-foreground">{template.job_category_name}</p>
                    ) : null}
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="h-7 px-2 text-xs"
                    onClick={() => onManageMembers?.(String(template.id))}
                    disabled={!onManageMembers}
                  >
                    Members ({memberCountByTemplateId.get(String(template.id)) ?? 0})
                  </Button>
                  <div className="flex gap-1">
                    {!template.active ? <Badge variant="secondary">Inactive</Badge> : null}
                    <Badge variant="outline">{template.required_headcount ?? template.slots?.length ?? 0} roles</Badge>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1 text-xs text-muted-foreground">
                {(template.slots ?? []).map((slot) => (
                  <p key={String(slot.id)}>
                    {slot.quantity ?? 1} × {slot.slot_label || slot.skill_role_name || slot.skill_role_unique_id}
                    {slot.is_primary ? ' • primary' : ''}
                  </p>
                ))}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(template)}>Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => deleteMutation.mutate(template.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit team' : 'Create team'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={name} onChange={(event) => handleNameChange(event.target.value)} placeholder="3 Ton AC Installation Team" />
              </div>
              <div className="space-y-2">
                <Label>Code</Label>
                <Input
                  value={code}
                  onChange={(event) => handleCodeChange(event.target.value)}
                  placeholder="Auto-generated from name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Job Category</Label>
              <Select value={jobCategory} onValueChange={handleJobCategoryChange} disabled={categoriesQuery.isLoading}>
                <SelectTrigger>
                  <SelectValue placeholder={categoriesQuery.isLoading ? 'Loading job categories...' : 'Any job category'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Any job category</SelectItem>
                  {categoryOptions.length === 0 && !categoriesQuery.isLoading ? (
                    <SelectItem value="__empty" disabled>No job categories found</SelectItem>
                  ) : (
                    categoryOptions.map((category) => (
                      <SelectItem key={String(category.id)} value={String(category.id)}>{category.name}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={2} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={active} onCheckedChange={setActive} />
              <Label>Active</Label>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Team role slots</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setSlots((current) => [...current, emptySlot(current.length)])}
                >
                  Add slot
                </Button>
              </div>
              {slots.map((slot, index) => (
                <div key={index} className="space-y-3 rounded-lg border p-3">
                  <div className="space-y-2">
                    <Label>Skill role</Label>
                    <Select
                      value={slot.skill_role}
                      onValueChange={(value) => setSlots((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, skill_role: value } : row))}
                      disabled={rolesQuery.isLoading}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={rolesQuery.isLoading ? 'Loading skill roles...' : 'Select skill role'} />
                      </SelectTrigger>
                      <SelectContent>
                        {slot.skill_role
                          && slot.skill_role_name
                          && !roleOptions.some((role) => String(role.id) === slot.skill_role) ? (
                          <SelectItem value={slot.skill_role}>{slot.skill_role_name}</SelectItem>
                        ) : null}
                        {roleOptions.length === 0 ? (
                          <SelectItem value="__empty" disabled>
                            {rolesQuery.isLoading ? 'Loading skill roles...' : 'No skill roles found'}
                          </SelectItem>
                        ) : (
                          roleOptions.map((role) => (
                            <SelectItem key={String(role.id)} value={String(role.id)}>{role.name}</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-[88px_minmax(0,1fr)_auto_auto] sm:items-end">
                    <div className="space-y-2">
                      <Label>Qty</Label>
                      <Input
                        value={slot.quantity}
                        onChange={(event) => setSlots((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, quantity: event.target.value } : row))}
                        placeholder="1"
                      />
                    </div>
                    <div className="min-w-0 space-y-2">
                      <Label>Slot label</Label>
                      <Input
                        value={slot.slot_label}
                        onChange={(event) => setSlots((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, slot_label: event.target.value } : row))}
                        placeholder="Optional label"
                      />
                    </div>
                    <div className="flex h-10 items-center gap-2 sm:h-auto sm:pb-0.5">
                      <Switch
                        checked={slot.is_primary}
                        onCheckedChange={(checked) => setSlots((current) => current.map((row, rowIndex) => ({
                          ...row,
                          is_primary: rowIndex === index ? checked : (checked ? false : row.is_primary),
                        })))}
                      />
                      <span className="text-xs whitespace-nowrap">Primary</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="shrink-0 self-end"
                      onClick={() => setSlots((current) => current.filter((_, rowIndex) => rowIndex !== index))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !name.trim()}>
              Save team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
